/**
 * Shared logic for "X wins in Y days" used in Coach banner, journey summary, and progress.
 *
 * Wins count is determined by:
 * 1. Backend (when available): learningPlan.targetWins or dashboard.targetWins (number)
 * 2. Profile: number of "wins you're chasing" (bio.winsLookingFor.length), capped by totalDays
 * 3. Plan has a single win: 1
 * 4. Default: 2
 *
 * Total days: dashboard.totalDays, or sum of plan daily contents, or 7.
 *
 * Backend API (optional): To control the number of wins from the backend, add one of:
 * - Learning plan: targetWins?: number
 * - Dashboard (GET .../user-learning-plans/users/{id}/dashboard): targetWins?: number
 * The frontend will use that value when present.
 */

import type { LearningPlan } from '@/types/learningManagement';
import type { ActivePlanDashboardResponse } from '@/types/learningManagement';
import type { UserProfile } from '@/types/userProfile';

const DEFAULT_DAYS = 7;
const DEFAULT_WINS = 2;

/** Total number of days from plan = sum of all daily contents across modules. */
export function getTotalDaysFromPlan(plan: LearningPlan | null): number {
  if (!plan?.learningModules?.length) return DEFAULT_DAYS;
  const total = plan.learningModules.reduce(
    (sum, mod) => sum + (Array.isArray(mod.dailyContents) ? mod.dailyContents.length : 0),
    0
  );
  return total > 0 ? total : (plan.learningModules?.length ?? DEFAULT_DAYS);
}

export interface WinsAndDays {
  winsCount: number;
  totalDays: number;
  /** "X wins in Y days" with correct singular/plural */
  label: string;
}

/**
 * Compute wins count and total days for the user.
 * Backend can add targetWins / winsCount on learning plan or dashboard for full control.
 */
export function getWinsAndDays(
  profile: UserProfile | null,
  learningPlan: LearningPlan | null,
  dashboard: ActivePlanDashboardResponse | null
): WinsAndDays {
  const totalDays =
    (dashboard?.totalDays != null && dashboard.totalDays > 0)
      ? dashboard.totalDays
      : getTotalDaysFromPlan(learningPlan);

  const planRaw = learningPlan as Record<string, unknown> | null | undefined;
  const dashboardRaw = dashboard as Record<string, unknown> | null | undefined;
  const fromPlan = typeof planRaw?.targetWins === 'number' ? planRaw.targetWins : undefined;
  const fromDashboard = typeof dashboardRaw?.targetWins === 'number' ? dashboardRaw.targetWins : undefined;
  const fromBackend = fromPlan ?? fromDashboard;

  let winsCount: number;
  if (typeof fromBackend === 'number' && fromBackend >= 0) {
    winsCount = Math.min(fromBackend, totalDays);
  } else {
    const profileWins = profile?.bio && typeof profile.bio === 'object' && Array.isArray((profile.bio as Record<string, unknown>).winsLookingFor)
      ? (profile.bio as Record<string, unknown>).winsLookingFor as unknown[]
      : [];
    const fromProfile = Math.min(profileWins.length, totalDays);
    if (fromProfile > 0) {
      winsCount = fromProfile;
    } else if (learningPlan?.win?.winId || learningPlan?.win?.winDescription) {
      winsCount = 1;
    } else {
      winsCount = Math.min(DEFAULT_WINS, totalDays);
    }
  }

  const dayWord = totalDays === 1 ? 'day' : 'days';
  const winWord = winsCount === 1 ? 'win' : 'wins';
  const label = `${winsCount} ${winWord} in ${totalDays} ${dayWord}`;

  return { winsCount, totalDays, label };
}
